/**
 * Branded loading spinner. Uses the existing logo image (no extra asset
 * download — already in `public/`) with a subtle pulse + fade animation.
 *
 * Drop-in replacement for the various "Loading…" text bits sprinkled
 * across the app. Three sizes via the `size` prop:
 *   - sm  → inline / button-adjacent (32px)
 *   - md  → page section placeholder (default, 64px)
 *   - lg  → full-screen route fallback (96px)
 */
interface Props {
  size?: "sm" | "md" | "lg";
  /** Optional caption shown below the logo. */
  label?: string;
  /** When true, fills the full viewport for a route-level fallback. */
  fullScreen?: boolean;
}

const SIZE_PX: Record<NonNullable<Props["size"]>, number> = {
  sm: 32,
  md: 64,
  lg: 96,
};

export function Spinner({ size = "md", label, fullScreen = false }: Props) {
  const px = SIZE_PX[size];

  const inner = (
    <div className="flex flex-col items-center justify-center gap-3">
      {/* The logo pulses gently so the user knows the page hasn't frozen.
          Two animations stacked: a slow opacity pulse and a faint scale
          breath. Pure CSS — no extra JS, no animation library. */}
      <img
        src="/pronerdz-logo.png"
        alt="ProNerdz"
        width={px}
        height={px}
        className="pronerdz-spinner-logo"
        style={{
          height: `${px}px`,
          width: "auto",
        }}
      />
      {label && (
        <span className="text-xs uppercase tracking-[0.18em] text-ink-400">
          {label}
        </span>
      )}
      <style>{`
        .pronerdz-spinner-logo {
          animation: pronerdz-pulse 1.6s ease-in-out infinite;
          will-change: opacity, transform;
        }
        @keyframes pronerdz-pulse {
          0%, 100% { opacity: 0.55; transform: scale(0.97); }
          50%      { opacity: 1;    transform: scale(1.03); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pronerdz-spinner-logo { animation: none; opacity: 0.85; }
        }
      `}</style>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        {inner}
      </div>
    );
  }
  return <div className="py-8 flex items-center justify-center">{inner}</div>;
}
